import { ApiError } from '../../api/client';
import { refreshSessionSingleFlight } from '../../api/tokenRefresh';
import {
  getCurrentUser,
  logout as logoutRequest,
  type CurrentUser,
  type TokenPairResponse,
} from '../../api/auth';
import { clearTokens, loadTokens } from './authStorage';

export type SessionRestoreOutcome =
  | { status: 'restored'; tokens: TokenPairResponse; user: CurrentUser }
  /** No usable session: storage is empty or the backend rejected the tokens. */
  | { status: 'anonymous' }
  /** Backend unreachable/failing: tokens are kept so the caller can retry. */
  | { status: 'unavailable' };

type UserLookup =
  | { status: 'ok'; user: CurrentUser }
  | { status: 'unauthorized' }
  | { status: 'forbidden' }
  | { status: 'unavailable' };

export function requireAdminProfile(currentUser: CurrentUser): CurrentUser {
  if (currentUser.has_admin_profile !== true) {
    throw new ApiError('Administrator access required.', 403);
  }
  return currentUser;
}

export async function discardTokens(tokens: TokenPairResponse): Promise<void> {
  try {
    await logoutRequest(tokens.refresh_token);
  } catch {
    // Local access must still be removed if backend token revocation fails.
  }
  clearTokens();
}

function hasStatus(error: unknown, status: number): boolean {
  return error instanceof ApiError && error.status === status;
}

async function fetchAdminUser(accessToken: string): Promise<UserLookup> {
  try {
    return { status: 'ok', user: requireAdminProfile(await getCurrentUser(accessToken)) };
  } catch (error) {
    if (hasStatus(error, 401)) {
      return { status: 'unauthorized' };
    }
    if (hasStatus(error, 403)) {
      return { status: 'forbidden' };
    }
    // Network failure, 5xx, proxy error: the session is fine, the backend is not.
    return { status: 'unavailable' };
  }
}

/**
 * One attempt at turning stored tokens back into an authenticated admin session.
 * Never clears tokens for transient failures — only an explicit backend rejection
 * (expired/revoked refresh token, or a non-admin account) ends the session.
 */
export async function attemptSessionRestore(): Promise<SessionRestoreOutcome> {
  const stored = loadTokens();
  if (!stored) {
    return { status: 'anonymous' };
  }

  const initial = await fetchAdminUser(stored.access_token);
  if (initial.status === 'ok') {
    return { status: 'restored', tokens: loadTokens() ?? stored, user: initial.user };
  }
  if (initial.status === 'forbidden') {
    await discardTokens(stored);
    return { status: 'anonymous' };
  }
  if (initial.status === 'unavailable') {
    return { status: 'unavailable' };
  }

  const refreshed = await refreshSessionSingleFlight();
  if (refreshed.status === 'expired') {
    return { status: 'anonymous' };
  }
  if (refreshed.status === 'unavailable') {
    return { status: 'unavailable' };
  }

  const renewed = loadTokens();
  if (!renewed) {
    return { status: 'anonymous' };
  }

  const retried = await fetchAdminUser(renewed.access_token);
  if (retried.status === 'ok') {
    return { status: 'restored', tokens: renewed, user: retried.user };
  }
  if (retried.status === 'forbidden') {
    await discardTokens(renewed);
    return { status: 'anonymous' };
  }
  if (retried.status === 'unauthorized') {
    // A just-issued access token was rejected: the session is genuinely broken.
    clearTokens();
    return { status: 'anonymous' };
  }
  return { status: 'unavailable' };
}
